import { useMemo } from 'react';

import { EMPTY_PREFERENCES } from '../constants/preferences.const';
import { type PrintInitiation } from '../types/print.type';
import { getPrinterNamesForInitiation } from '../utils/mappers.util';
import { conciliatePreferences, resolveDefaultPrinterName } from '../utils/printPreferences.util';
import { useLocalPrinters } from './useLocalPrinters';
import { usePrintSetup } from './usePrintSetup';
import { useShippoPrintMetadata } from './useShippoPrintMetadata';
import { useWorkstation } from './useWorkstation';

const EMPTY_PRINTER_NAMES: string[] = [];

export type UsePrinterConfigOptions = {
  initiation?: PrintInitiation;
};

export const usePrinterConfig = ({ initiation }: UsePrinterConfigOptions = {}) => {
  const { lastKnownHostname } = usePrintSetup();
  const {
    connected,
    defaultPrinter,
    hostname: qzHostname,
    isFetched: isFetchedMetadata,
  } = useShippoPrintMetadata();

  const hostname = connected ? qzHostname : lastKnownHostname ?? undefined;

  const {
    isFetched: isLocalPrintersFetched,
    isFetching: isLocalPrintersFetching,
    printers: localPrinters,
  } = useLocalPrinters();
  const {
    query: { isFetched: isWorkstationFetched, isFetching: isWorkstationFetching },
    workstation,
  } = useWorkstation({ hostname });

  const defaults = workstation?.preferences?.defaults;

  const resolvedDefaultPrinter = useMemo(() => {
    if (!isLocalPrintersFetched || !isFetchedMetadata) return null;

    return resolveDefaultPrinterName(defaultPrinter, localPrinters);
  }, [defaultPrinter, isFetchedMetadata, isLocalPrintersFetched, localPrinters]);

  const isFetching = connected
    ? isWorkstationFetching || !isFetchedMetadata || isLocalPrintersFetching
    : isWorkstationFetching;
  // Keep cached preferences available during background refetches so print settings UI does not blink.
  const isReady = connected && isFetchedMetadata && isWorkstationFetched && isLocalPrintersFetched;

  const preferences = useMemo(() => {
    // Keep cached preferences available during background refetches so print settings UI does not blink.
    if (!isWorkstationFetched) return EMPTY_PREFERENCES;

    return conciliatePreferences(connected ? resolvedDefaultPrinter : null, defaults ?? {});
  }, [connected, defaults, isWorkstationFetched, resolvedDefaultPrinter]);

  const printerNames = useMemo(
    () =>
      isReady && initiation !== undefined
        ? getPrinterNamesForInitiation(preferences, initiation)
        : EMPTY_PRINTER_NAMES,
    [initiation, isReady, preferences],
  );

  return {
    hostname,
    isFetching,
    isReady,
    isWorkstationFetched,
    preferences,
    printerNames,
    workstation: workstation ?? null,
  };
};
